import { useEffect, useState } from 'react';

interface HeroProps {
  onMascotClick?: (category: 'pizzas' | 'burgers' | 'italienne') => void;
}

export default function Hero({ onMascotClick }: HeroProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [mascotReady, setMascotReady] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const timer = setTimeout(() => setMascotReady(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const handlePizzaMascotClick = () => {
    if (onMascotClick) {
      onMascotClick('italienne');
    }
  };

  const handleBurgerMascotClick = () => {
    if (onMascotClick) {
      onMascotClick('burgers');
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pb-32">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#F5EBE0] via-[#F0E4DA] to-[#E8D5D0]"></div>
      
      {/* Decorative Shapes */}
      <div className="absolute top-16 right-10 w-72 h-72 bg-[#C85A54]/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-32 left-8 w-56 h-56 bg-[#C85A54]/8 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 right-1/4 w-40 h-40 bg-[#D4B5AE]/20 rounded-full blur-2xl"></div>

      {/* Mascot - Burger (Left Side) - Déplacé légèrement vers la gauche */}
      <div 
        className={`absolute left-2 sm:left-6 md:left-12 lg:left-20 top-24 sm:top-28 md:top-32 z-20 ${mascotReady ? 'animate-mascot-enter-left' : 'opacity-0'}`}
      >
        <div className="relative">
          <div className={`${mascotReady ? 'animate-mascot-walk' : ''}`}>
            <div 
              onClick={handleBurgerMascotClick}
              className="w-24 h-24 sm:w-28 sm:h-28 md:w-36 md:h-36 lg:w-44 lg:h-44 cursor-pointer hover:scale-110 transition-transform duration-300"
            >
              <img 
                src="https://static.readdy.ai/image/2dcf709829f9212ac45256ebe1ad3ecc/619f7ab72f4ba2a6a21d72885e5f7a12.png" 
                alt="Mascotte Burger Pizza JO" 
                title="Mascotte Burger Pizz'Ajo"
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
          </div>
          {/* Shadow under mascot */}
          <div className="animate-mascot-shadow w-16 sm:w-20 md:w-24 lg:w-28 h-3 bg-[#6B4E3D]/15 rounded-full mx-auto mt-1 blur-sm"></div>
        </div>
      </div>

      {/* Mascot - Pizza (Right Side) */}
      <div 
        className={`absolute right-6 sm:right-12 md:right-20 lg:right-32 top-24 sm:top-28 md:top-32 z-20 ${mascotReady ? 'animate-mascot-enter-right' : 'opacity-0'}`}
        style={{ animationDelay: '0.3s' }}
      >
        <div className="relative">
          {/* Speech bubble nouveauté pizza */}
          {mascotReady && (
            <div
              onClick={handlePizzaMascotClick}
              className="absolute -top-14 right-0 cursor-pointer group/bubble"
              style={{ zIndex: 10 }}
            >
              <div className="relative bg-white rounded-xl px-2.5 py-1.5 shadow-md border border-terracotta/20 hover:border-terracotta/60 transition-all duration-300 hover:scale-105 animate-bounce-slow max-w-[120px]">
                <p className="text-[9px] font-semibold text-terracotta leading-snug">
                  Nouveauté
                </p>
                <p className="text-[10px] font-bold text-chocolate leading-tight">
                  🍕 L'Italienne
                </p>
                {/* Triangle pointer */}
                <div className="absolute -bottom-2 right-4 w-0 h-0" style={{
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: '7px solid white',
                  filter: 'drop-shadow(0 1px 1px rgba(139,90,43,0.15))'
                }}></div>
              </div>
            </div>
          )}
          <div className={`${mascotReady ? 'animate-mascot-wave' : ''}`}>
            <div 
              onClick={handlePizzaMascotClick}
              className="w-24 h-24 sm:w-28 sm:h-28 md:w-36 md:h-36 lg:w-44 lg:h-44 cursor-pointer hover:scale-110 transition-transform duration-300"
            >
              <img 
                src="https://static.readdy.ai/image/2dcf709829f9212ac45256ebe1ad3ecc/af7ffc491ab108979cc09280d0515b78.png" 
                alt="Mascotte Pizza Pizza JO" 
                title="Mascotte Pizza Pizz'Ajo"
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
          </div>
          {/* Shadow under mascot */}
          <div className="animate-mascot-shadow w-16 sm:w-20 md:w-24 lg:w-28 h-3 bg-[#6B4E3D]/15 rounded-full mx-auto mt-1 blur-sm"></div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`relative z-10 text-center px-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        
        {/* Decorative Line Above */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-16 h-0.5 bg-[#B8463F]/40"></div>
          <div className="w-2 h-2 rounded-full bg-[#B8463F]"></div>
          <div className="w-16 h-0.5 bg-[#B8463F]/40"></div>
        </div>

        {/* Main Title - PIZZA JO */}
        <h1 className="font-display text-7xl sm:text-8xl md:text-9xl lg:text-[140px] font-bold text-[#B8463F] mb-6 tracking-wide leading-none" style={{ textShadow: '2px 4px 8px rgba(107, 78, 61, 0.15)' }}>
          Pizz&apos;Ajo
        </h1>

        {/* Tagline */}
        <p className={`text-lg md:text-xl text-[#6B4E3D] max-w-lg mx-auto font-light leading-relaxed mb-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          Un go&ucirc;t qui vous donne envie de revenir, encore et encore
        </p>

        {/* Decorative Line Below */}
        <div className="w-24 h-px bg-[#6B4E3D]/30 mx-auto mb-10"></div>

        {/* Phone Number - Enlarged and Animated */}
        <a 
          href="tel:0360532792" 
          className="inline-flex items-center gap-4 text-[#6B4E3D] hover:text-[#B8463F] transition-all duration-300 cursor-pointer mb-3 group animate-phone-pulse"
        >
          <span className="w-14 h-14 flex items-center justify-center bg-[#B8463F]/15 rounded-full group-hover:bg-[#B8463F]/25 transition-all duration-300 group-hover:scale-110">
            <i className="ri-phone-fill text-2xl text-[#B8463F]"></i>
          </span>
          <span className="text-3xl md:text-4xl font-bold tracking-wide group-hover:scale-105 transition-transform duration-300">03 60 53 27 92</span>
        </a>

        {/* Rappel commander par téléphone */}
        <p className="text-[#6B4E3D]/70 text-sm mb-12 flex items-center justify-center gap-2">
          <i className="ri-phone-line text-base text-[#B8463F]"></i>
          Pensez à appeler pour commander
        </p>
      </div>

      {/* Action Buttons - Bottom */}
      <div className={`relative z-10 flex flex-col sm:flex-row items-center justify-center gap-4 px-6 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        
        {/* Voir notre carte & Choisir */}
        <a 
          href="#menu" 
          className="flex items-center gap-2 bg-[#B8463F] text-[#F5EBE0] px-8 py-3 rounded-full text-sm font-semibold shadow-lg shadow-[#B8463F]/25 hover:shadow-xl hover:shadow-[#B8463F]/35 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-restaurant-line text-base"></i>
          Voir notre carte &amp; choisir
        </a>

      </div>

      {/* Bottom Decorative Element — Cadre dispos */}
      <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-30 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="bg-[#FDF8F3]/90 backdrop-blur-sm rounded-2xl px-6 py-4 border-2 border-terracotta/20 shadow-sm">
          {/* Ligne titre */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <i className="ri-alert-fill text-sm text-terracotta"></i>
            <span className="text-xs font-bold text-chocolate uppercase tracking-wider">Quand commander ?</span>
          </div>
          {/* Séparateur */}
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-terracotta/30 to-transparent mx-auto mb-3"></div>
          {/* Dispos */}
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 bg-terracotta/8 rounded-lg px-3 py-1.5">
              <div className="w-6 h-6 flex items-center justify-center bg-terracotta/15 rounded-md">
                <i className="ri-restaurant-line text-sm text-terracotta"></i>
              </div>
              <div>
                <p className="text-[11px] font-bold text-chocolate leading-tight">Pizzas</p>
                <p className="text-[10px] text-terracotta font-semibold leading-tight">Mardi → Dimanche</p>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-chocolate/6 rounded-lg px-3 py-1.5">
              <div className="w-6 h-6 flex items-center justify-center bg-chocolate/12 rounded-md">
                <i className="ri-restaurant-2-fill text-sm text-chocolate"></i>
              </div>
              <div>
                <p className="text-[11px] font-bold text-chocolate leading-tight">Burgers</p>
                <p className="text-[10px] text-chocolate font-semibold leading-tight">Mardi → Jeudi</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
